import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, apiFetch } from './client'

/**
 * Admin Retell phones — TanStack hooks for the per-org Retell phone +
 * agent provisioning UI.
 *
 * Mirrors :file:`admin-phones.ts` for VAPI but the surface is smaller:
 *
 * * The backend endpoints are *per-org* (``/orgs/{org_id}/retell-phones``),
 *   so every query/mutation here is parameterized by ``orgId``. The TanStack
 *   query keys include ``orgId`` so switching orgs doesn't show stale data
 *   from the previous selection.
 * * There is no Retell-side "sync" — phones are added one-by-one by an
 *   operator, with the e164 + optional ``termination_uri`` typed in
 *   directly. Until Retell exposes a list endpoint on the BYOT side this
 *   stays a manual provisioning flow.
 * * There is no equivalent of ``/api/admin/vapi-assistants`` for Retell
 *   today — the binding modal takes a free-text ``agent_id`` paste from
 *   the operator's Retell dashboard. If/when a list endpoint lands, the
 *   binding modal can swap the text input for a ``<select>``.
 *
 * Error surfacing: the shared ``apiFetch`` throws an :class:`ApiError`
 * carrying the parsed body. Most of the Retell admin endpoints' errors
 * are flat ``{detail: {error, message}}`` shapes (no multi-field
 * conflicts like VAPI's 1:1 phone gate), so the consumer just reads
 * ``(err as ApiError).detail`` directly — no dedicated parser needed.
 */

// ── Shapes ─────────────────────────────────────────────────────────────────

export type RetellPhoneAgentKind = 'inbound' | 'outbound' | 'both'

export interface AdminRetellPhoneAgent {
  id: string
  phone_id: string
  agent_id: string
  kind: RetellPhoneAgentKind
  // ``purpose`` is normalized to ``null`` at the API surface (the underlying
  // column stores the empty string as the "default-for-kind" sentinel —
  // see ``retell_phone_repo._purpose_to_sql``).
  purpose: string | null
  weight: number
  created_at: number
}

export interface AdminRetellPhone {
  id: string
  org_id: string
  e164: string
  termination_uri: string | null
  label: string | null
  last_synced_at: number | null
  created_at: number
  agents: AdminRetellPhoneAgent[]
}

export interface AdminRetellPhonesResponse {
  org_id: string
  phones: AdminRetellPhone[]
}

// ── Query keys ─────────────────────────────────────────────────────────────

// Per-org key — switching the org-selector should refetch, not reuse stale.
const QK_RETELL_PHONES = (orgId: string) =>
  ['admin', 'retell-phones', orgId] as const

const ADMIN_BASE = '/api/admin'

// ── Reads ──────────────────────────────────────────────────────────────────

/**
 * Fetch all Retell phones (joined with their agent bindings) for an org.
 *
 * Disabled when ``orgId`` is empty so an empty org selector doesn't fire a
 * 404. The hook is otherwise side-effect-free — no mutations.
 */
export function useAdminRetellPhones(orgId: string) {
  return useQuery({
    queryKey: QK_RETELL_PHONES(orgId),
    queryFn: () =>
      apiFetch<AdminRetellPhonesResponse>(
        `${ADMIN_BASE}/orgs/${encodeURIComponent(orgId)}/retell-phones`,
      ),
    // Don't fire when no org is selected — the dropdown defaults to "" and
    // the API would respond 404.
    enabled: orgId.length > 0,
  })
}

// ── Mutations ──────────────────────────────────────────────────────────────

export interface CreateRetellPhoneArgs {
  e164: string
  termination_uri?: string | null
  label?: string | null
}

/**
 * Register a new Retell phone under an org.
 *
 * Backend returns 201 with the inserted row (no ``agents`` key — bindings
 * are added separately). We invalidate the per-org cache so the inventory
 * card refreshes automatically.
 */
export function useCreateRetellPhone(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (args: CreateRetellPhoneArgs) =>
      apiFetch<Omit<AdminRetellPhone, 'agents'>>(
        `${ADMIN_BASE}/orgs/${encodeURIComponent(orgId)}/retell-phones`,
        {
          method: 'POST',
          body: JSON.stringify({
            e164: args.e164,
            termination_uri: args.termination_uri ?? null,
            label: args.label ?? null,
          }),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK_RETELL_PHONES(orgId) })
    },
  })
}

export interface BindRetellAgentArgs {
  agent_id: string
  kind: RetellPhoneAgentKind
  purpose?: string | null
}

export interface BindRetellAgentResponse {
  ok: boolean
  created: boolean
  binding: {
    binding_id: string
    phone_id: string
    agent_id: string
    kind: RetellPhoneAgentKind
    purpose: string | null
    weight: number
  }
}

/**
 * Bind a Retell agent to one of the org's registered phones.
 *
 * Idempotent on ``(phone_id, agent_id, kind, purpose)`` — the response's
 * ``created`` flag tells the operator whether a new row was inserted or
 * the duplicate was a no-op. Either way the binding is returned so the UI
 * can surface confirmation.
 */
export function useBindRetellAgent(orgId: string, phoneId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (args: BindRetellAgentArgs) =>
      apiFetch<BindRetellAgentResponse>(
        `${ADMIN_BASE}/orgs/${encodeURIComponent(orgId)}/retell-phones/${encodeURIComponent(phoneId)}/agents`,
        {
          method: 'POST',
          body: JSON.stringify({
            agent_id: args.agent_id,
            kind: args.kind,
            purpose: args.purpose ?? null,
            // ``weight`` is intentionally not exposed in the UI — defaults
            // to 1 server-side, sufficient until the operator needs to
            // tune dispatch priority.
          }),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK_RETELL_PHONES(orgId) })
    },
  })
}

// ── Error helpers ──────────────────────────────────────────────────────────

/**
 * Extract a human-readable error message from a thrown :class:`ApiError`.
 *
 * Backend returns ``{detail: {error, message}}`` for these endpoints; we
 * prefer ``detail.message`` (operator-facing copy) and fall back to the
 * raw ``Error.message`` only when the response isn't in that shape.
 */
export function getRetellApiErrorMessage(err: unknown): string | null {
  if (!(err instanceof Error)) return null
  if (err instanceof ApiError) {
    const detail = err.detail as { message?: unknown; error?: unknown } | undefined
    if (detail && typeof detail.message === 'string') return detail.message
    if (detail && typeof detail.error === 'string') return detail.error
  }
  return err.message
}
